import { propagation } from '@opentelemetry/api';
import {
  W3CTraceContextPropagator,
  W3CBaggagePropagator,
  CompositePropagator,
} from '@opentelemetry/core';
import { ApolloLink } from '@apollo/client';
import * as Sentry from '@sentry/react';

// Initialize OpenTelemetry propagators
const compositePropagator = new CompositePropagator({
  propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
});

// Set the global propagator
propagation.setGlobalPropagator(compositePropagator);

// Helper function to get trace context from Sentry
const getSentryTraceContext = () => {
  try {
    // First try to get the active span
    const span = Sentry.getActiveSpan();
    if (span) {
      const spanContext = span.spanContext();
      if (spanContext) {
        return {
          traceId: spanContext.traceId,
          spanId: spanContext.spanId,
          traceFlags: spanContext.traceFlags,
        };
      }
    }

    // Fallback: try to get from current scope's propagation context
    const scope = Sentry.getCurrentScope();
    if (scope) {
      const propagationContext = scope.getPropagationContext();
      if (propagationContext && propagationContext.traceId && propagationContext.parentSpanId) {
        return {
          traceId: propagationContext.traceId,
          spanId: propagationContext.parentSpanId,
          traceFlags: propagationContext.sampled ? 1 : 0,
        };
      }
    }

    // Last resort: try to parse from Sentry's trace header
    try {
      const sentryTraceHeader = Sentry.getTraceData();
      if (sentryTraceHeader && sentryTraceHeader['sentry-trace']) {
        const sentryTrace = sentryTraceHeader['sentry-trace'];
        // Sentry trace format: {traceId}-{spanId}-{sampled}
        const match = sentryTrace.match(/^([0-9a-f]{32})-([0-9a-f]{16})-([01])$/);
        if (match) {
          return {
            traceId: match[1],
            spanId: match[2],
            traceFlags: parseInt(match[3], 10),
          };
        }
      }
    } catch (error) {
      // Silent
    }
  } catch (error) {
    // Silent
  }
  return null;
};

// Create a custom Apollo Link that injects trace headers
export const tracingLink = new ApolloLink((operation, forward) => {
  // Try to get trace context from Sentry first
  const sentryTraceContext = getSentryTraceContext();

  if (sentryTraceContext) {
    // Create W3C Trace Context headers manually
    const traceparent = `00-${sentryTraceContext.traceId}-${sentryTraceContext.spanId}-${sentryTraceContext.traceFlags?.toString(16).padStart(2, '0') || '01'}`;

    const traceHeaders: Record<string, string> = {
      traceparent: traceparent,
    };

    // Add tracestate header with Sentry vendor data if available
    try {
      const scope = Sentry.getCurrentScope();
      if (scope) {
        // Add basic Sentry tracestate entry
        traceHeaders['tracestate'] = 'sentry=sampled';
      }
    } catch (e) {
      // Tracestate not available, continue without it
    }

    // Add the headers to the operation context
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...traceHeaders,
      },
    }));
  }

  return forward(operation);
});

// Helper function to manually inject headers for fetch requests
export const injectTraceHeaders = (
  headers: Record<string, string> = {}
): Record<string, string> => {
  const sentryTraceContext = getSentryTraceContext();

  if (sentryTraceContext) {
    const traceparent = `00-${sentryTraceContext.traceId}-${sentryTraceContext.spanId}-${sentryTraceContext.traceFlags?.toString(16).padStart(2, '0') || '01'}`;

    const traceHeaders: Record<string, string> = {
      traceparent: traceparent,
    };

    // Add tracestate header with Sentry vendor data if available
    try {
      const scope = Sentry.getCurrentScope();
      if (scope) {
        // Add basic Sentry tracestate entry
        traceHeaders['tracestate'] = 'sentry=sampled';
      }
    } catch (e) {
      // Tracestate not available, continue without it
    }

    return {
      ...headers,
      ...traceHeaders,
    };
  }

  return headers;
};
